import {useEffect, useMemo, useState} from 'react'
import {IconSearch, IconX} from '@tabler/icons-react'
import {apiGet} from './api/client'
import {useApiToken} from './api/useApiToken'
import {looksVideo} from './api/media'
import {nameMatches, searchWords} from './api/tree'
import type {TreeIndex} from './api/types'
import Overlay from './Overlay'

interface Item {
  handle: string
  gramps_id: string
  desc?: string
  mime?: string
}

interface Props {
  ix: TreeIndex
  /** кому выбираем аватарку */
  person: string
  /** что уже есть у человека — такие снимки отмечаем, чтобы не искать заново */
  have: string[]
  onPick: (handle: string) => void
  onClose: () => void
}

/**
 * Выбор снимка из архива.
 *
 * Нужен потому, что нужная фотография чаще всего уже загружена — на ней
 * просто отмечен кто-то другой. Раньше за этим приходилось уходить в
 * галерею и привязывать человека оттуда; из карточки взять готовое было
 * нельзя, только залить файл заново — и в архиве появлялся второй объект
 * на тот же кадр.
 */
export default function MediaPicker({ix, person, have, onPick, onClose}: Props) {
  const jwt = useApiToken()
  const [items, setItems] = useState<Item[] | null>(null)
  const [q, setQ] = useState('')
  const [err, setErr] = useState('')
  /* По умолчанию показываем только своё и ничьё: аватарку выбирают из
     снимков этого человека, а весь архив — это сотня чужих лиц, среди
     которых своё искать дольше, чем оно того стоит. Переключатель
     оставлен: групповой снимок, где отмечен пока только брат, тоже
     годится в аватарку, и запирать от него не за что. */
  const [onlyMine, setOnlyMine] = useState(true)

  useEffect(() => {
    let alive = true
    apiGet<Item[]>('/media/', {pagesize: 20000})
      .then(m => alive && setItems(m))
      .catch(e => alive && setErr(e instanceof Error ? e.message : 'Не удалось загрузить архив'))
    return () => { alive = false }
  }, [])

  /* Ищем и по подписи, и по людям на снимке: чаще всего помнят не
     подпись, а кто на фотографии. */
  const owners = useMemo(() => {
    const map = new Map<string, string[]>()
    for (const p of ix.people.values()) {
      for (const r of p.media_list || []) {
        const list = map.get(r.ref)
        if (list) list.push(p.handle)
        else map.set(r.ref, [p.handle])
      }
    }
    return map
  }, [ix])

  /** Свои и ничьи — то, из чего обычно и выбирают. */
  const mine = useMemo(() => (items || []).filter(m => {
    const who = owners.get(m.handle) || []
    return who.includes(person) || who.length === 0
  }), [items, owners, person])

  const shown = useMemo(() => {
    const words = searchWords(q)
    // ролики в аватарки не годятся: миниатюр у них нет
    const pics = (onlyMine ? mine : items || []).filter(m => !looksVideo(m.mime))
    if (!words.length) return pics
    return pics.filter(m => {
      const cap = (m.desc || '').toLowerCase()
      if (words.every(w => cap.includes(w))) return true
      return (owners.get(m.handle) || []).some(h => nameMatches(ix.people.get(h), words))
    })
  }, [items, mine, onlyMine, q, owners, ix])

  return (
    <Overlay>
      <div className="modal-bg" onClick={onClose}>
        <div className="modal wide" onClick={e => e.stopPropagation()}>
          <div className="mhead">
            <h2>Снимок из архива</h2>
            <button type="button" className="close" onClick={onClose} aria-label="Закрыть">
              <IconX size={18} stroke={1.8} />
            </button>
          </div>

          <div className="pickhead">
            <div className="search">
              <IconSearch size={15} className="ico" />
              <input value={q} onChange={e => setQ(e.target.value)} autoFocus
                     placeholder="Подпись или имя человека на снимке" />
            </div>
            <button type="button" className={'chip' + (onlyMine ? ' on' : '')}
                    onClick={() => setOnlyMine(v => !v)}
                    title="Свои снимки и те, что пока ни к кому не привязаны">
              Свои и ничьи <em>{mine.length}</em>
            </button>
          </div>

          {err && <div className="err inline">{err}</div>}
          {!items && !err && <div className="muted">Загружаем…</div>}

          {items && (
            <div className="pickgrid">
              {shown.map(m => (
                <button type="button" key={m.handle}
                        className={'pickone' + (have.includes(m.handle) ? ' has' : '')}
                        title={m.desc || m.gramps_id}
                        onClick={() => onPick(m.handle)}>
                  {/* Без loading="lazy": окно прокручивается само, и
                      браузер считал невидимым почти всё содержимое —
                      снимки не начинали грузиться, пока не долистаешь. */}
                  <img src={`/api/media/${m.handle}/thumbnail/240?jwt=${jwt}`}
                       alt="" width={104} height={104} />
                  {have.includes(m.handle) && <span className="hasmark">уже есть</span>}
                </button>
              ))}
              {!shown.length && <div className="empty">Ничего не нашлось</div>}
            </div>
          )}

          <span className="hint">
            Снимок станет главным для этого человека. Если его у него ещё не
            было, он заодно к нему привяжется — второго объекта на тот же
            кадр в архиве не появится. Снимите отбор, если нужный кадр
            отмечен пока на ком-то другом.
          </span>
        </div>
      </div>
    </Overlay>
  )
}
